import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Divider,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

import type { HighlightSegment, OverlayTrackLayer, Tack, TrackPoint } from '../../types';
import { formatMetricLabel, formatMetricLabelNoUnit } from '../../utils/metricLabels';

import './ScatterPanel.css';
import './themeInversion.css';

const COLOR_PRIMARY = '#4dabf7';
const COLOR_PORT = '#ef4444';
const COLOR_STARBOARD = '#22c55e';
const COLOR_HIGHLIGHT = '#f5a524';
const COLOR_REFERENCE_STROKE = '#60a5fa';
const GRID_COLOR = 'var(--borderSubtle, rgba(148, 163, 184, 0.35))';
const TEXT_COLOR = 'var(--textContrastHigh, var(--contrast, #1f2937))';
const AXIS_TICK_COLOR = 'var(--textMutedStrong, rgba(148, 163, 184, 0.85))';
const CHART_MARGIN = { top: 16, right: 24, bottom: 24, left: 16 };
const SCATTER_INTERACTION_AREA = 144;

const DEFAULT_X_METRIC = 'tws';
const DEFAULT_Y_METRIC = 'bsp';
const DEFAULT_PHASE_SECONDS = 30;
const DEFAULT_CURVE_DEGREE = 1;
const MAX_CURVE_DEGREE = 3;
const STD_FILTER_MIN = 0.5;
const STD_FILTER_MAX = 5;
const STD_FILTER_STEP = 0.5;
const HIGHLIGHT_PERCENTILE = 0.9;

const PHASE_OPTIONS = [30, 60, 120];

const AXIS_LABEL_STYLE = {
  fill: TEXT_COLOR,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.02em',
};
const AXIS_TICK_STYLE = {
  fill: AXIS_TICK_COLOR,
  fontSize: 11,
  fontWeight: 500,
};

const I18N_KEY_SCATTER_BOAT = 'race.scatter.series.boat' as const;

interface ScatterPanelProperties {
  trackData: TrackPoint[];
  tacks: Tack[];
  relatedTrackLayers?: OverlayTrackLayer[];
  primaryBoatName?: string | null;
  highlightSegments?: HighlightSegment[];
}

interface ScatterDatum {
  x: number;
  y: number;
  timestamp: number;
  startUtc: string;
  endUtc: string;
  durationSeconds: number;
  count: number;
  twa: number | null;
  vmg: number | null;
  vmgpc: number | null;
  colorGroup: 'port' | 'starboard' | 'unknown';
  datasetKey?: string;
  datasetLabel?: string;
}

interface ChartDatum extends ScatterDatum {
  fitY?: number | null;
  fitDash?: number | null;
}

interface ScatterSeries {
  key: string;
  label: string;
  color: string;
  data: ScatterDatum[];
}

interface DatasetConfig {
  key: string;
  label: string;
  color: string;
  trackData: TrackPoint[];
  tacks: Tack[];
}

type TimeWindow = {
  startMs: number;
  endMs: number;
};

interface DatasetView extends DatasetConfig {
  aggregated: ScatterDatum[];
  filtered: ScatterDatum[];
  highlightSeries: ScatterDatum[];
  highlightThreshold: number | null;
  axisDomain: { x: [number, number]; y: [number, number] } | null;
  referenceArea: {
    x1: number;
    x2: number;
    y1: number;
    y2: number;
  } | null;
  polynomialFit: PolynomialResult | null;
  verticalFit: VerticalFit | null;
  chartData: ChartDatum[];
  regressionData: Array<{ x: number; y: number }> | null;
  scatterSeries: ScatterSeries[];
}

interface PolynomialResult {
  data: Array<{ x: number; y: number }>;
  degree: number;
  coefficients: number[];
}

interface VerticalFit {
  data: Array<{ x: number; y: number }>;
  x: number;
}

type ScatterShapeProps = {
  cx?: number;
  cy?: number;
  fill?: string;
  stroke?: string;
  isActive?: boolean;
  payload?: ScatterDatum;
} & Record<string, unknown>;

type LegendEntry = {
  key: string;
  label: string;
  color: string;
  type: 'circle' | 'diamond' | 'line';
};

const toFinite = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const average = (values: number[]): number => {
  if (values.length === 0) {
    return 0;
  }
  let total = 0;
  for (const value of values) {
    total += value;
  }
  return total / values.length;
};

const computeStdDev = (values: number[]): number => {
  if (values.length <= 1) {
    return 0;
  }
  const avg = average(values);
  let varianceSum = 0;
  for (const value of values) {
    varianceSum += (value - avg) ** 2;
  }
  const variance = varianceSum / Math.max(values.length - 1, 1);
  return Math.sqrt(variance);
};

const buildGlobalIndexMap = (trackData: TrackPoint[]): Map<number, number> => {
  const map = new Map<number, number>();
  for (const [index, point] of trackData.entries()) {
    const global = typeof point.globalIndex === 'number' ? point.globalIndex : index;
    map.set(global, index);
  }
  return map;
};

const collectExcludedIndices = (tacks: Tack[], trackData: TrackPoint[]): Set<number> => {
  const excluded = new Set<number>();
  if (!Array.isArray(tacks) || tacks.length === 0 || trackData.length === 0) {
    return excluded;
  }
  const reference = buildGlobalIndexMap(trackData);
  for (const tack of tacks) {
    const start = reference.get((tack as { start_index?: number }).start_index ?? -1);
    const end = reference.get((tack as { end_index?: number }).end_index ?? -1);
    if (start == null || end == null) {
      continue;
    }
    const min = Math.min(start, end);
    const max = Math.max(start, end);
    for (let index = min; index <= max; index += 1) {
      excluded.add(index);
    }
  }
  return excluded;
};

const getMetricValue = (point: TrackPoint, metric: string): unknown =>
  (point as unknown as Record<string, unknown>)[metric];

const aggregateSample = (
  indices: number[],
  trackData: TrackPoint[],
  xMetric: string,
  yMetric: string,
): ScatterDatum | null => {
  if (indices.length === 0) {
    return null;
  }
  const xValues: number[] = [];
  const yValues: number[] = [];
  let twaSum = 0;
  let twaCount = 0;
  let vmgSum = 0;
  let vmgCount = 0;
  let vmgPcSum = 0;
  let vmgPcCount = 0;
  for (const index of indices) {
    const point = trackData[index];
    const xValue = toFinite(getMetricValue(point, xMetric));
    const yValue = toFinite(getMetricValue(point, yMetric));
    if (xValue != null) {
      xValues.push(xValue);
    }
    if (yValue != null) {
      yValues.push(yValue);
    }
    const twa = toFinite(point.twa);
    if (twa != null) {
      twaSum += twa;
      twaCount += 1;
    }
    const vmg = toFinite(point.vmg);
    if (vmg != null) {
      vmgSum += vmg;
      vmgCount += 1;
    }
    const vmgPc = toFinite(point.vmgpc);
    if (vmgPc != null) {
      vmgPcSum += vmgPc;
      vmgPcCount += 1;
    }
  }
  if (xValues.length === 0 || yValues.length === 0) {
    return null;
  }
  const first = trackData[indices[0]];
  const last = trackData[indices.at(-1)!];
  const averageTwa = twaCount > 0 ? twaSum / twaCount : null;
  const colorGroup: ScatterDatum['colorGroup'] =
    averageTwa == null ? 'unknown' : averageTwa >= 0 ? 'starboard' : 'port';

  return {
    x: average(xValues),
    y: average(yValues),
    timestamp: new Date(last.utc ?? last.date ?? Date.now()).getTime(),
    startUtc: first.utc ?? first.date ?? '',
    endUtc: last.utc ?? last.date ?? '',
    durationSeconds: Math.max(
      0,
      Math.round(
        (new Date(last.utc ?? last.date ?? Date.now()).getTime() -
          new Date(first.utc ?? first.date ?? Date.now()).getTime()) /
          1000,
      ),
    ),
    count: indices.length,
    twa: twaCount > 0 ? twaSum / twaCount : null,
    vmg: vmgCount > 0 ? vmgSum / vmgCount : null,
    vmgpc: vmgPcCount > 0 ? vmgPcSum / vmgPcCount : null,
    colorGroup,
  };
};

const aggregateTrackData = (
  trackData: TrackPoint[],
  xMetric: string,
  yMetric: string,
  phaseDurationSeconds: number,
  excludeManeuvers: boolean,
  tacks: Tack[],
): ScatterDatum[] => {
  if (!Array.isArray(trackData) || trackData.length === 0) {
    return [];
  }
  const excluded = excludeManeuvers ? collectExcludedIndices(tacks, trackData) : null;
  const samples: ScatterDatum[] = [];
  const bucket: number[] = [];
  let currentBucketStart = 0;
  const target = Math.max(phaseDurationSeconds, 0) * 1000;
  const flushBucket = () => {
    const sample = aggregateSample(bucket, trackData, xMetric, yMetric);
    if (sample) {
      samples.push(sample);
    }
    bucket.length = 0;
    currentBucketStart = 0;
  };
  for (let index = 0; index < trackData.length; index += 1) {
    if (excluded?.has(index)) {
      continue;
    }
    const point = trackData[index];
    const time = new Date(point.utc ?? point.date ?? Date.now()).getTime();
    if (!Number.isFinite(time)) {
      continue;
    }
    if (phaseDurationSeconds <= 0) {
      const single = aggregateSample([index], trackData, xMetric, yMetric);
      if (single) {
        samples.push(single);
      }
      continue;
    }
    if (bucket.length === 0) {
      bucket.push(index);
      currentBucketStart = time;
      continue;
    }
    bucket.push(index);
    if (time - currentBucketStart >= target) {
      flushBucket();
    }
  }
  if (bucket.length > 0 && phaseDurationSeconds > 0) {
    flushBucket();
  }
  return samples;
};

const computeFilterBounds = (points: ScatterDatum[]) => {
  if (points.length === 0) {
    return null;
  }
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  return {
    meanX: average(xs),
    meanY: average(ys),
    stdX: computeStdDev(xs),
    stdY: computeStdDev(ys),
  };
};

const filterByStandardDeviation = (
  points: ScatterDatum[],
  bounds: ReturnType<typeof computeFilterBounds>,
  multiplier: number | null,
): ScatterDatum[] => {
  if (!bounds || multiplier == null || bounds.stdX === 0 || bounds.stdY === 0) {
    return points;
  }
  const { meanX, stdX, meanY, stdY } = bounds;
  return points.filter(
    (point) =>
      Math.abs(point.x - meanX) <= stdX * multiplier &&
      Math.abs(point.y - meanY) <= stdY * multiplier,
  );
};

const computeQuantile = (values: number[], quantile: number): number | null => {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor(quantile * sorted.length)));
  return sorted[index] ?? null;
};

const evaluatePolynomial = (coefficients: number[], x: number): number => {
  let total = 0;
  for (const [index, coefficient] of coefficients.entries()) {
    total += coefficient * x ** index;
  }
  return total;
};

const solveLinearSystem = (matrix: number[][], vector: number[]): number[] | null => {
  const size = vector.length;
  const augmented = matrix.map((row, index) => [...row, vector[index]]);
  for (let col = 0; col < size; col += 1) {
    let pivot = col;
    let pivotValue = Math.abs(augmented[col][col]);
    for (let row = col + 1; row < size; row += 1) {
      const value = Math.abs(augmented[row][col]);
      if (value > pivotValue) {
        pivotValue = value;
        pivot = row;
      }
    }
    if (pivotValue < 1e-9) {
      return null;
    }
    if (pivot !== col) {
      const temp = augmented[col];
      augmented[col] = augmented[pivot];
      augmented[pivot] = temp;
    }
    const pivotRow = augmented[col];
    const divisor = pivotRow[col];
    for (let c = col; c <= size; c += 1) {
      pivotRow[c] /= divisor;
    }
    for (let row = 0; row < size; row += 1) {
      if (row === col) {
        continue;
      }
      const factor = augmented[row][col];
      for (let c = col; c <= size; c += 1) {
        augmented[row][c] -= factor * pivotRow[c];
      }
    }
  }
  return augmented.map((row) => row[size]);
};

const computePolynomialFit = (
  points: ScatterDatum[],
  requestedDegree: number,
): PolynomialResult | null => {
  if (points.length < 2) {
    return null;
  }
  const maxDegree = Math.min(requestedDegree, Math.min(MAX_CURVE_DEGREE, points.length - 1));
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const buildMatrices = (degree: number) => {
    const size = degree + 1;
    const matrix: number[][] = Array.from({ length: size }, () =>
      Array.from({ length: size }, () => 0),
    );
    const vector: number[] = Array.from({ length: size }, () => 0);
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        let sum = 0;
        for (const x of xs) {
          sum += x ** (row + col);
        }
        matrix[row][col] = sum;
      }
      let projection = 0;
      for (const [index, x] of xs.entries()) {
        projection += ys[index] * x ** row;
      }
      vector[row] = projection;
    }
    return { matrix, vector };
  };
  const tryDegree = (degree: number): PolynomialResult | null => {
    const { matrix, vector } = buildMatrices(degree);
    const coefficients = solveLinearSystem(matrix, vector);
    if (!coefficients) {
      return null;
    }
    const samplePoints = [...xs]
      .sort((a, b) => a - b)
      .map((x) => ({ x, y: evaluatePolynomial(coefficients, x) }));
    return {
      data: samplePoints,
      degree,
      coefficients,
    };
  };
  for (let degree = maxDegree; degree >= 1; degree -= 1) {
    const fit = tryDegree(degree);
    if (fit) {
      return fit;
    }
  }
  return null;
};

const computeRegressionData = (
  fitCurve: boolean,
  polynomialFit: PolynomialResult | null,
  chartData: ChartDatum[],
  points: ScatterDatum[],
) => {
  if (!fitCurve) {
    return null;
  }
  if (polynomialFit?.data && polynomialFit.data.length >= 2) {
    return polynomialFit.data;
  }
  if (polynomialFit?.coefficients && polynomialFit.degree === 1) {
    const xsFromChart = chartData.map((d) => d.x).filter((value): value is number => Number.isFinite(value));
    const xs =
      xsFromChart.length >= 2
        ? xsFromChart
        : points.map((point) => point.x).filter((value): value is number => Number.isFinite(value));
    if (xs.length < 2) {
      return null;
    }
    const [b0, b1] = polynomialFit.coefficients;
    const x1 = Math.min(...xs);
    const x2 = Math.max(...xs);
    const y1 = b0 + b1 * x1;
    const y2 = b0 + b1 * x2;
    if (!Number.isFinite(y1) || !Number.isFinite(y2)) {
      return null;
    }
    return [
      { x: x1, y: y1 },
      { x: x2, y: y2 },
    ];
  }
  return null;
};

const buildDatasetSeries = (
  datasetKey: string,
  label: string,
  color: string,
  points: ScatterDatum[],
  colorByTack: boolean,
  t: ReturnType<typeof useTranslation>['t'],
): ScatterSeries[] => {
  if (!colorByTack) {
    return [
      {
        key: `${datasetKey}-all`,
        label,
        color,
        data: points,
      },
    ];
  }
  const starboard = points.filter((point) => point.colorGroup === 'starboard');
  const port = points.filter((point) => point.colorGroup === 'port');
  const unknown = points.filter((point) => point.colorGroup === 'unknown');
  const starboardColor = deriveTackTint(color, 'starboard');
  const portColor = deriveTackTint(color, 'port');
  const unknownColor = deriveTackTint(color, 'unknown');
  const series: ScatterSeries[] = [];
  if (starboard.length > 0) {
    series.push({
      key: `${datasetKey}-starboard`,
      label: `${label} · ${t('race.scatter.series.starboard', 'Starboard')}`,
      color: starboardColor,
      data: starboard,
    });
  }
  if (port.length > 0) {
    series.push({
      key: `${datasetKey}-port`,
      label: `${label} · ${t('race.scatter.series.port', 'Port')}`,
      color: portColor,
      data: port,
    });
  }
  if (unknown.length > 0) {
    series.push({
      key: `${datasetKey}-unknown`,
      label: `${label} · ${t('race.scatter.series.unknown', 'Unknown')}`,
      color: unknownColor,
      data: unknown,
    });
  }
  if (series.length === 0) {
    series.push({
      key: `${datasetKey}-all`,
      label,
      color,
      data: points,
    });
  }
  return series;
};

const buildReferenceArea = (
  bounds: ReturnType<typeof computeFilterBounds>,
  showReferenceArea: boolean,
) => {
  if (!showReferenceArea || !bounds) {
    return null;
  }
  const { meanX, stdX, meanY, stdY } = bounds;
  if (stdX === 0 || stdY === 0) {
    return null;
  }
  return {
    x1: meanX - stdX,
    x2: meanX + stdX,
    y1: meanY - stdY,
    y2: meanY + stdY,
  };
};

const buildChartDataForPoints = (
  points: ScatterDatum[],
  polynomialFit: PolynomialResult | null,
): ChartDatum[] => {
  if (points.length === 0) {
    return [];
  }
  const sorted = [...points].sort((a, b) => a.x - b.x);
  if (!polynomialFit?.coefficients) {
    return sorted;
  }
  return sorted.map((point) => ({
    ...point,
    fitY: evaluatePolynomial(polynomialFit.coefficients, point.x),
  }));
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const parseHexColor = (hex: string): { r: number; g: number; b: number } | null => {
  if (typeof hex !== 'string') {
    return null;
  }
  const normalized = hex.trim().replace('#', '');
  if (![3, 6].includes(normalized.length) || /[^\da-f]/i.test(normalized)) {
    return null;
  }
  const expanded = normalized.length === 3 ? [...normalized].map((char) => char + char).join('') : normalized;
  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);
  return Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b) ? null : { r, g, b };
};

const toHexChannel = (value: number): string =>
  Math.round(Math.min(255, Math.max(0, value)))
    .toString(16)
    .padStart(2, '0');

const rgbToHex = (r: number, g: number, b: number): string => `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;

const blendHexColors = (baseHex: string, overlayHex: string, overlayWeight: number): string | null => {
  const base = parseHexColor(baseHex);
  const overlay = parseHexColor(overlayHex);
  if (!base || !overlay) {
    return null;
  }
  const weight = clamp01(overlayWeight);
  const inverse = 1 - weight;
  return rgbToHex(
    base.r * inverse + overlay.r * weight,
    base.g * inverse + overlay.g * weight,
    base.b * inverse + overlay.b * weight,
  );
};

const deriveTackTint = (boatColor: string, variant: ScatterDatum['colorGroup']): string => {
  if (variant === 'starboard') {
    return blendHexColors(COLOR_STARBOARD, boatColor, 0.35) ?? COLOR_STARBOARD;
  }
  if (variant === 'port') {
    return blendHexColors(COLOR_PORT, boatColor, 0.35) ?? COLOR_PORT;
  }
  return boatColor || COLOR_PRIMARY;
};

const hexToRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(
    normalized.length === 3 ? [...normalized].map((char) => char + char).join('') : normalized,
    16,
  );
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ScatterPoint: React.FC<{
  cx?: number;
  cy?: number;
  fill?: string;
  stroke?: string;
  isActive?: boolean;
  isHovered?: boolean;
  payload?: ScatterDatum;
  variant?: 'default' | 'highlight';
  onHover?: (payload: ScatterDatum, anchor: { clientX: number; clientY: number }) => void;
  onLeave?: () => void;
}> = ({
  cx,
  cy,
  fill,
  stroke,
  isActive,
  isHovered,
  payload,
  variant = 'default',
  onHover,
  onLeave,
}) => {
  if (cx == null || cy == null) {
    return null;
  }
  const baseRadius = variant === 'highlight' ? 6 : 4;
  const isPointActive = Boolean(isActive || isHovered);
  const radius = isPointActive ? baseRadius + 1.5 : baseRadius;
  const glowRadius = radius + 3;
  const classNames = ['scatter-panel__point'];
  if (variant === 'highlight') {
    classNames.push('scatter-panel__point--highlight');
  }
  if (isPointActive) {
    classNames.push('is-active');
  }
  type ScatterPointPointerEvent =
    | React.MouseEvent<SVGGElement>
    | React.PointerEvent<SVGGElement>
    | React.TouchEvent<SVGGElement>;

  const extractClientPoint = (event: ScatterPointPointerEvent): { clientX: number; clientY: number } | null => {
    if ('touches' in event) {
      const touch = event.touches[0] ?? event.changedTouches[0];
      if (!touch) {
        return null;
      }
      return { clientX: touch.clientX, clientY: touch.clientY };
    }
    return { clientX: event.clientX, clientY: event.clientY };
  };

  const handlePointerEnter = (event: ScatterPointPointerEvent) => {
    if (!payload) {
      return;
    }
    const anchor = extractClientPoint(event);
    if (!anchor) {
      return;
    }
    onHover?.(payload, anchor);
  };
  const handlePointerLeave = () => {
    onLeave?.();
  };
  return (
    <g
      className={classNames.join(' ')}
      transform={`translate(${cx}, ${cy})`}
      style={{ '--point-color': fill ?? stroke ?? COLOR_PRIMARY } as React.CSSProperties}
      onMouseEnter={handlePointerEnter}
      onMouseLeave={handlePointerLeave}
      onTouchStart={handlePointerEnter}
      onTouchEnd={handlePointerLeave}
      onTouchCancel={handlePointerLeave}
    >
      <circle className="scatter-panel__point-glow" r={glowRadius} />
      <circle className="scatter-panel__point-core" r={radius} />
    </g>
  );
};

const ScatterLegendBar: React.FC<{
  entries: LegendEntry[];
  hiddenKeys: Set<string>;
  onToggle: (key: string) => void;
}> = ({ entries, hiddenKeys, onToggle }) => {
  if (entries.length === 0) {
    return null;
  }
  return (
    <div className="scatter-panel__legend-bar" role="list">
      {entries.map((entry) => {
        const isHidden = hiddenKeys.has(entry.key);
        return (
          <button
            type="button"
            key={entry.key}
            className="scatter-panel__legend-chip"
            data-hidden={isHidden}
            onClick={() => onToggle(entry.key)}
            style={{ '--legend-color': entry.color } as React.CSSProperties}
          >
            <span
              className="scatter-panel__legend-chip-swatch"
              data-legend-type={entry.type}
            />
            <span className="scatter-panel__legend-chip-label">{entry.label}</span>
          </button>
        );
      })}
    </div>
  );
};


const formatTimeWithTenths = (date: Date | null): string => {
  if (!date) {
    return '';
  }
  const base = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  const periodMatch = base.match(/\s?(AM|PM)$/u);
  const suffix = periodMatch?.[0] ?? '';
  const timePart = periodMatch ? base.slice(0, -suffix.length).trimEnd() : base;
  const tenths = Math.floor(date.getMilliseconds() / 100);
  const trimmedSuffix = suffix.trim();
  const suffixPart = trimmedSuffix ? ` ${trimmedSuffix}` : '';
  if (tenths <= 0) {
    return `${timePart}${suffixPart}`.trim();
  }
  return `${timePart}.${tenths}${suffixPart}`.trim();
};

const ScatterTooltip: React.FC<{
  datum: ScatterDatum | null;
  xMetricLabel: string;
  yMetricLabel: string;
  phaseDuration: number;
}> = ({ datum, xMetricLabel, yMetricLabel, phaseDuration }) => {
  const { t } = useTranslation('common');
  if (!datum) {
    return null;
  }
  const boatLabel = datum.datasetLabel ?? t(I18N_KEY_SCATTER_BOAT, 'Boat');
  const boatTitle = t('race.scatter.tooltip.boatLabel', 'Boat');
  const vmgPercent = toFinite(datum.vmgpc);
  const end = datum.endUtc ? new Date(datum.endUtc) : null;
  const start = datum.startUtc ? new Date(datum.startUtc) : null;
  const referenceTime = end ?? start;
  const formattedTime = formatTimeWithTenths(referenceTime);
  const effectiveDuration =
    datum.durationSeconds > 0
      ? datum.durationSeconds
      : phaseDuration > 0
        ? phaseDuration
        : 0;
  const metricRows: Array<{ label: string; value: number }> = [
    { label: xMetricLabel, value: datum.x },
    { label: yMetricLabel, value: datum.y },
  ];
  if (vmgPercent != null) {
    metricRows.push({
      label: t('race.scatter.tooltip.vmgPercent', 'VMG%'),
      value: vmgPercent,
    });
  }
  return (
    <div className="scatter-tooltip">
      <div className="scatter-tooltip__title">
        {boatTitle}: {boatLabel}
      </div>
      <div className="scatter-tooltip__metrics">
        {metricRows.map((row) => (
          <div className="scatter-tooltip__metric" key={row.label}>
            <span className="scatter-tooltip__metric-label">{row.label}</span>
            <span className="scatter-tooltip__metric-value">{row.value.toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const getMetricOptions = (trackData: TrackPoint[]): Array<{ value: string; label: string }> => {
  if (!Array.isArray(trackData) || trackData.length === 0) {
    return [];
  }
  const candidates = new Set<string>();
  const excluded = new Set(['utc', 'lat', 'lon', 'globalIndex']);
  for (const point of trackData) {
    for (const key of Object.keys(point)) {
      if (excluded.has(key)) {
        continue;
      }
      const value = getMetricValue(point, key);
      if (toFinite(value) != null) {
        candidates.add(key);
      }
    }
  }
  return [...candidates]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: formatMetricLabel(value) }));
};
type TooltipAlignment = {
  horizontal: 'start' | 'center' | 'end';
  vertical: 'top' | 'bottom';
};

const ScatterPanelComponent: React.FC<ScatterPanelProperties> = ({
  trackData,
  tacks,
  relatedTrackLayers = [],
  primaryBoatName,
  highlightSegments,
}) => {
  const { t } = useTranslation('common');
  const chartWrapperRef = useRef<HTMLDivElement | null>(null);
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState<Set<string>>(new Set());
  const primaryBoatLabel = useMemo(() => {
    const trimmed = primaryBoatName?.trim();
    if (trimmed && trimmed.length > 0) {
      return trimmed;
    }
    return t(I18N_KEY_SCATTER_BOAT, 'Boat');
  }, [primaryBoatName, t]);

  const highlightRanges = useMemo(() => {
    if (!Array.isArray(trackData) || trackData.length === 0) {
      return null;
    }
    const ranges: Array<{ start: number; end: number }> = [];
    if (Array.isArray(highlightSegments)) {
      for (const segment of highlightSegments) {
        if (typeof segment.startIndex !== 'number' || typeof segment.endIndex !== 'number') {
          continue;
        }
        const lower = Math.min(segment.startIndex, segment.endIndex);
        const upper = Math.max(segment.startIndex, segment.endIndex);
        const start = Math.max(0, Math.min(trackData.length - 1, lower));
        const end = Math.max(0, Math.min(trackData.length - 1, upper));
        if (end < start) {
          continue;
        }
        ranges.push({ start, end });
      }
    }
    if (ranges.length === 0) {
      return null;
    }
    ranges.sort((a, b) => a.start - b.start);
    const merged: Array<{ start: number; end: number }> = [];
    for (const range of ranges) {
      const last = merged.at(-1);
      if (!last || range.start > last.end + 1) {
        merged.push({ ...range });
      } else {
        last.end = Math.max(last.end, range.end);
      }
    }
    return merged;
  }, [highlightSegments, trackData]);

  const selectionWindows = useMemo<TimeWindow[] | null>(() => {
    if (!highlightRanges || highlightRanges.length === 0) {
      return null;
    }
    const windows: TimeWindow[] = [];
    for (const range of highlightRanges) {
      const startPoint = trackData[range.start];
      const endPoint = trackData[range.end];
      if (!startPoint?.utc || !endPoint?.utc) {
        continue;
      }
      const startMs = Date.parse(startPoint.utc);
      const endMs = Date.parse(endPoint.utc);
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        continue;
      }
      windows.push({
        startMs: Math.min(startMs, endMs),
        endMs: Math.max(startMs, endMs),
      });
    }
    if (windows.length === 0) {
      return null;
    }
    windows.sort((a, b) => a.startMs - b.startMs);
    const merged: TimeWindow[] = [{ ...windows[0] }];
    for (const window of windows.slice(1)) {
      const last = merged.at(-1);
      if (!last) {
        merged.push({ ...window });
      } else if (window.startMs <= last.endMs) {
        last.endMs = Math.max(last.endMs, window.endMs);
      } else {
        merged.push({ ...window });
      }
    }
    return merged;
  }, [highlightRanges, trackData]);

  const primaryTrackData = useMemo(() => {
    if (!highlightRanges) {
      return trackData;
    }
    const subset: TrackPoint[] = [];
    for (const range of highlightRanges) {
      subset.push(...trackData.slice(range.start, range.end + 1));
    }
    return subset;
  }, [highlightRanges, trackData]);

  const metricOptions = useMemo(() => getMetricOptions(trackData), [trackData]);

  const defaultXMetric = metricOptions.some((option) => option.value === DEFAULT_X_METRIC)
    ? DEFAULT_X_METRIC
    : metricOptions[0]?.value ?? DEFAULT_X_METRIC;
  const defaultYMetric =
    metricOptions.find((option) => option.value === DEFAULT_Y_METRIC && option.value !== defaultXMetric)
      ?.value ?? metricOptions.find((option) => option.value !== defaultXMetric)?.value ?? DEFAULT_Y_METRIC;

  const [xMetric, setXMetric] = useState(defaultXMetric);
  const [yMetric, setYMetric] = useState(defaultYMetric);
  const [phaseDuration, setPhaseDuration] = useState(DEFAULT_PHASE_SECONDS);
  const [fitCurve, setFitCurve] = useState(true);
  const [curveDegree, setCurveDegree] = useState(DEFAULT_CURVE_DEGREE);
  const [showReferenceArea, setShowReferenceArea] = useState(true);
  const [colorByTack, setColorByTack] = useState(false);
  const [highlightTopVmg, setHighlightTopVmg] = useState(false);
  const [excludeManeuvers, setExcludeManeuvers] = useState(false);
  const [stdFilter, setStdFilter] = useState<number | null>(2);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState<{
    datum: ScatterDatum;
    position: { left: number; top: number };
    alignment: TooltipAlignment;
  } | null>(null);

  const handleStdFilterStep = useCallback(
    (direction: 1 | -1) => {
      setStdFilter((current) => {
        const base = typeof current === 'number' ? current : STD_FILTER_MIN;
        const next = Math.min(STD_FILTER_MAX, Math.max(STD_FILTER_MIN, base + direction * STD_FILTER_STEP));
        return Number(next.toFixed(2));
      });
    },
    [setStdFilter],
  );

  const handlePointEnter = useCallback((datum: ScatterDatum, anchor: { clientX: number; clientY: number }) => {
    const wrapperRect = chartWrapperRef.current?.getBoundingClientRect();
    if (!wrapperRect) {
      return;
    }
    const relativeX = anchor.clientX - wrapperRect.left;
    const relativeY = anchor.clientY - wrapperRect.top;
    const clampedX = Math.min(Math.max(relativeX, 8), wrapperRect.width - 8);
    const clampedY = Math.min(Math.max(relativeY, 8), wrapperRect.height - 8);
    const horizontal: TooltipAlignment['horizontal'] =
      clampedX < 140 ? 'start' : clampedX > wrapperRect.width - 140 ? 'end' : 'center';
    const vertical: TooltipAlignment['vertical'] = clampedY < 120 ? 'bottom' : 'top';
    setHoveredPoint({
      datum,
      position: { left: clampedX, top: clampedY },
      alignment: { horizontal, vertical },
    });
  }, []);

  const handlePointLeave = useCallback(() => {
    setHoveredPoint(null);
  }, []);

  const toggleSeriesVisibility = useCallback((key: string) => {
    if (!key) {
      return;
    }
    setHiddenSeriesKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const renderScatterPoint = useCallback(
    (props: unknown) => {
      const shapeProps = props as ScatterShapeProps;
      const isHovered = hoveredPoint?.datum === shapeProps.payload;
      return (
        <ScatterPoint
          cx={shapeProps.cx}
          cy={shapeProps.cy}
          fill={shapeProps.fill}
          stroke={shapeProps.stroke}
          isActive={Boolean(shapeProps.isActive)}
          payload={shapeProps.payload}
          isHovered={isHovered}
          onHover={handlePointEnter}
          onLeave={handlePointLeave}
        />
      );
    },
    [handlePointEnter, handlePointLeave, hoveredPoint],
  );

  const renderHighlightPoint = useCallback(
    (props: unknown) => {
      const shapeProps = props as ScatterShapeProps;
      const isHovered = hoveredPoint?.datum === shapeProps.payload;
      return (
        <ScatterPoint
          cx={shapeProps.cx}
          cy={shapeProps.cy}
          fill={shapeProps.fill}
          stroke={shapeProps.stroke}
          isActive={Boolean(shapeProps.isActive)}
          payload={shapeProps.payload}
          variant="highlight"
          isHovered={isHovered}
          onHover={handlePointEnter}
          onLeave={handlePointLeave}
        />
      );
    },
    [handlePointEnter, handlePointLeave, hoveredPoint],
  );

  const datasetConfigs = useMemo<DatasetConfig[]>(() => {
    const overlayBoatLabel = (label?: string | null): string => {
      if (typeof label !== 'string') {
        return t(I18N_KEY_SCATTER_BOAT, 'Boat');
      }
      const separatorIndex = label.indexOf('·');
      const base = (separatorIndex === -1 ? label : label.slice(0, separatorIndex)).trim();
      if (base.length > 0) {
        return base;
      }
      return label.trim().length > 0 ? label.trim() : t(I18N_KEY_SCATTER_BOAT, 'Boat');
    };

    const configs: DatasetConfig[] = [
      {
        key: 'primary',
        label: primaryBoatLabel,
        color: COLOR_PRIMARY,
        trackData: primaryTrackData,
        tacks,
      },
    ];
    for (const layer of relatedTrackLayers) {
      if (!layer.trackData || layer.trackData.length === 0) {
        continue;
      }
      configs.push({
        key: layer.selectionKey ?? `overlay-${layer.fileId}`,
        label: overlayBoatLabel(layer.label),
        color: layer.color,
        trackData: layer.trackData,
        tacks: [],
      });
    }
    return configs;
  }, [primaryTrackData, tacks, relatedTrackLayers, primaryBoatLabel, t]);

  const datasetViews = useMemo<DatasetView[]>(() => {
    return datasetConfigs.map((config) => {
      const aggregated = aggregateTrackData(
        config.trackData,
        xMetric,
        yMetric,
        phaseDuration,
        excludeManeuvers,
        config.tacks,
      );
      const bounds = computeFilterBounds(aggregated);
      const filtered = filterByStandardDeviation(aggregated, bounds, stdFilter);
      const annotatedFiltered = filtered.map((point) => ({
        ...point,
        datasetKey: config.key,
        datasetLabel: config.label,
      }));
      const highlightValues = highlightTopVmg
        ? annotatedFiltered
            .map((point) => toFinite(point.vmgpc ?? point.vmg))
            .filter((value): value is number => value != null)
        : [];
      const highlightThreshold = highlightTopVmg
        ? computeQuantile(highlightValues, HIGHLIGHT_PERCENTILE)
        : null;
      const highlightSeries =
        highlightThreshold == null
          ? []
          : annotatedFiltered.filter((point) => {
              const base = toFinite(point.vmgpc ?? point.vmg);
              return base != null && base >= highlightThreshold;
            });
      const axisDomain = (() => {
        if (annotatedFiltered.length === 0) {
          return null;
        }
        const xs = annotatedFiltered.map((point) => point.x);
        const ys = annotatedFiltered.map((point) => point.y);
        const spanX = Math.max(...xs) - Math.min(...xs);
        const spanY = Math.max(...ys) - Math.min(...ys);
        const paddingX = spanX === 0 ? 1 : spanX * 0.05;
        const paddingY = spanY === 0 ? 1 : spanY * 0.05;
        return {
          x: [Math.min(...xs) - paddingX, Math.max(...xs) + paddingX] as [number, number],
          y: [Math.min(...ys) - paddingY, Math.max(...ys) + paddingY] as [number, number],
        };
      })();
      const referenceArea = buildReferenceArea(bounds, showReferenceArea);
      const polynomialFit = fitCurve ? computePolynomialFit(annotatedFiltered, curveDegree) : null;
      const verticalFit = null;
      const chartData = buildChartDataForPoints(annotatedFiltered, polynomialFit);
      const regressionData = computeRegressionData(fitCurve, polynomialFit, chartData, annotatedFiltered);
      const scatterSeries = buildDatasetSeries(
        config.key,
        config.label,
        config.color,
        annotatedFiltered,
        colorByTack,
        t,
      );
      return {
        ...config,
        aggregated,
        filtered: annotatedFiltered,
        highlightSeries,
        highlightThreshold,
        axisDomain,
        referenceArea,
        polynomialFit,
        verticalFit,
        chartData,
        regressionData,
        scatterSeries,
      };
    });
  }, [
    datasetConfigs,
    xMetric,
    yMetric,
    phaseDuration,
    excludeManeuvers,
    stdFilter,
    highlightTopVmg,
    showReferenceArea,
    fitCurve,
    curveDegree,
    colorByTack,
    t,
  ]);

  const combinedPoints = useMemo(() => datasetViews.flatMap((view) => view.filtered), [datasetViews]);
  const curveLegendLabel = useMemo(
    () => t('race.scatter.series.curveShort', 'Fit curve'),
    [t],
  );

  const legendEntries = useMemo<LegendEntry[]>(() => {
    const entries: LegendEntry[] = [];
    for (const view of datasetViews) {
      for (const series of view.scatterSeries) {
        entries.push({
          key: series.key,
          label: series.label,
          color: series.color,
          type: 'circle',
        });
      }
      if (view.regressionData) {
        entries.push({
          key: `${view.key}-regression`,
          label: `${curveLegendLabel}`,
          color: view.color,
          type: 'line',
        });
      }
    }
    return entries;
  }, [curveLegendLabel, datasetViews, t]);

  useEffect(() => {
    setHiddenSeriesKeys((previous) => {
      if (previous.size === 0) {
        return previous;
      }
      const validKeys = new Set(legendEntries.map((entry) => entry.key));
      const next = new Set<string>();
      let changed = false;
      for (const key of previous) {
        if (validKeys.has(key)) {
          next.add(key);
        } else {
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [legendEntries]);

  const formatAxisTick = useCallback((value: number | string) => {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) {
      return '';
    }
    return Math.abs(numeric) < 1e-6 ? '0' : numeric.toFixed(1);
  }, []);

  const globalAxisDomain = useMemo(() => {
    const xs = combinedPoints.map((point) => point.x);
    const ys = combinedPoints.map((point) => point.y);
    if (xs.length === 0 || ys.length === 0) {
      return null;
    }
    const spanX = Math.max(...xs) - Math.min(...xs);
    const spanY = Math.max(...ys) - Math.min(...ys);
    const padX = spanX === 0 ? 1 : spanX * 0.05;
    const padY = spanY === 0 ? 1 : spanY * 0.05;
    return {
      x: [Math.min(...xs) - padX, Math.max(...xs) + padX] as [number, number],
      y: [Math.min(...ys) - padY, Math.max(...ys) + padY] as [number, number],
    };
  }, [combinedPoints]);

  const chartIsEmpty = combinedPoints.length === 0;

  useEffect(() => {
    if (chartIsEmpty) {
      setHoveredPoint(null);
    }
  }, [chartIsEmpty]);

  const buildSettingsSelect = (value: string, onChange: (value: string) => void, label: string) => (
    <Select
      label={label}
      value={value}
      onChange={(newValue) => {
        if (typeof newValue === 'string') {
          onChange(newValue);
        }
      }}
      data={metricOptions}
      comboboxProps={{ withinPortal: true }}
    />
  );

  const renderGridItem = (node: React.ReactNode, variant: 'default' | 'switch' = 'default') => (
    <div
      className={
        variant === 'switch'
          ? 'scatter-panel__grid-item scatter-panel__grid-item--switch'
          : 'scatter-panel__grid-item'
      }
    >
      {node}
    </div>
  );
  return (
    <div className="scatter-panel theme-inverted" data-mantine-color-scheme="light">
      <Stack gap="md" className="scatter-panel__content">
        <Group justify="space-between" align="center" className="scatter-panel__header">
          <Title order={4}>{t('race.scatter.title', 'Scatter chart')}</Title>
          <Button size="xs" variant="light" onClick={() => setSettingsOpen((open) => !open)}>
            {settingsOpen
              ? t('race.scatter.controls.hide', 'Hide settings')
              : t('race.scatter.controls.show', 'Show settings')}
          </Button>
        </Group>

        {settingsOpen && (
          <div className="scatter-panel__section scatter-panel__controls">
            <Stack gap="lg">
              <Stack gap={6}>
                <Text size="xs" className="scatter-panel__controls-title">
                  {t('race.scatter.controls.section.metrics', 'Axes & timeframe')}
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" className="scatter-panel__controls-grid">
                  {renderGridItem(buildSettingsSelect(xMetric, setXMetric, t('race.scatter.xAxis', 'X')))}
                  {renderGridItem(buildSettingsSelect(yMetric, setYMetric, t('race.scatter.yAxis', 'Y')))}
                  {renderGridItem(
                    <Select
                      label={t('race.scatter.phaseDuration', 'Duration')}
                      value={String(phaseDuration)}
                      data={PHASE_OPTIONS.map((value) => ({ value: String(value), label: `${value}s` }))}
                      onChange={(newValue) => {
                        const numeric = Number(newValue ?? DEFAULT_PHASE_SECONDS);
                        setPhaseDuration(Number.isFinite(numeric) ? numeric : DEFAULT_PHASE_SECONDS);
                      }}
                      comboboxProps={{ withinPortal: true }}
                    />,
                  )}
                </SimpleGrid>
              </Stack>
              <Divider className="scatter-panel__controls-divider" />
              <Stack gap={6} className="scatter-panel__controls-block scatter-panel__controls-block--visual">
                <Text size="xs" className="scatter-panel__controls-title">
                  {t('race.scatter.controls.section.visual', 'Visual options')}
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" className="scatter-panel__controls-grid">
                  {renderGridItem(
                    <Switch
                      label={t('race.scatter.fitCurve', 'Fit curve')}
                      checked={fitCurve}
                      onChange={(event) => setFitCurve(event.currentTarget.checked)}
                    />,
                    'switch',
                  )}
                  {renderGridItem(
                    <NumberInput
                      label={t('race.scatter.curveDegree', 'Order')}
                      value={curveDegree}
                      min={1}
                      max={MAX_CURVE_DEGREE}
                      disabled={!fitCurve}
                      onChange={(value) => {
                        const numeric = typeof value === 'number' ? value : Number(value);
                        if (Number.isFinite(numeric)) {
                          setCurveDegree(Math.min(MAX_CURVE_DEGREE, Math.max(1, numeric)));
                        }
                      }}
                    />,
                  )}
                  {renderGridItem(
                    <Switch
                      label={t('race.scatter.controls.referenceArea', 'Reference area')}
                      checked={showReferenceArea}
                      onChange={(event) => setShowReferenceArea(event.currentTarget.checked)}
                    />,
                    'switch',
                  )}
                  {renderGridItem(
                    <Switch
                      label={t('race.scatter.controls.colorByTack', 'Color by tack')}
                      checked={colorByTack}
                      onChange={(event) => setColorByTack(event.currentTarget.checked)}
                    />,
                    'switch',
                  )}
                  {renderGridItem(
                    <Switch
                      label={t('race.scatter.controls.excludeManeuvers', 'Exclude maneuvers')}
                      checked={excludeManeuvers}
                      onChange={(event) => setExcludeManeuvers(event.currentTarget.checked)}
                    />,
                    'switch',
                  )}
                  {renderGridItem(
                    <Switch
                      label={t('race.scatter.controls.highlightTop', 'Highlight top VMG%')}
                      checked={highlightTopVmg}
                      onChange={(event) => setHighlightTopVmg(event.currentTarget.checked)}
                    />,
                    'switch',
                  )}
                  {renderGridItem(
                    <NumberInput
                      label={t('race.scatter.controls.stdFilter', 'Filter')}
                      value={stdFilter ?? STD_FILTER_MIN}
                      min={STD_FILTER_MIN}
                      max={STD_FILTER_MAX}
                      step={STD_FILTER_STEP}
                      allowDecimal
                      onChange={(value) => {
                        if (value === '') {
                          setStdFilter(null);
                          return;
                        }
                        const numeric = Number(value);
                        setStdFilter(Number.isFinite(numeric) ? numeric : null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'ArrowUp') {
                          event.preventDefault();
                          handleStdFilterStep(1);
                        } else if (event.key === 'ArrowDown') {
                          event.preventDefault();
                          handleStdFilterStep(-1);
                        }
                      }}
                    />,
                  )}
                </SimpleGrid>
              </Stack>
            </Stack>
          </div>
        )}

        <Divider variant="dashed" className="scatter-panel__divider" />

        <div className="scatter-panel__chart-wrapper" ref={chartWrapperRef}>
          {chartIsEmpty ? (
            <div className="scatter-panel__empty">
              <Text size="sm">{t('race.scatter.emptyTitle', 'No data to display')}</Text>
              <Text size="xs" c="dimmed">
                {t('race.scatter.emptySubtitle', 'Adjust metrics or timeframe to see data.')}
              </Text>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart margin={CHART_MARGIN}>
                  <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    domain={globalAxisDomain?.x ?? ['auto', 'auto']}
                    tickFormatter={formatAxisTick}
                    tick={AXIS_TICK_STYLE}
                    label={{
                      value: formatMetricLabel(xMetric),
                      position: 'insideBottom',
                      offset: -12,
                      style: AXIS_LABEL_STYLE,
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    domain={globalAxisDomain?.y ?? ['auto', 'auto']}
                    tickFormatter={formatAxisTick}
                    tick={AXIS_TICK_STYLE}
                    label={{
                      value: formatMetricLabel(yMetric),
                      angle: -90,
                      position: 'insideLeft',
                      offset: -30,
                      style: AXIS_LABEL_STYLE,
                    }}
                  />
                  <ZAxis type="number" range={[SCATTER_INTERACTION_AREA, SCATTER_INTERACTION_AREA]} />
                  {datasetViews.map((view) =>
                    view.referenceArea ? (
                      <ReferenceArea
                        key={`${view.key}-area`}
                        x1={view.referenceArea.x1}
                        x2={view.referenceArea.x2}
                        y1={view.referenceArea.y1}
                        y2={view.referenceArea.y2}
                        fill={hexToRgba(view.color, 0.08)}
                        stroke={view.color}
                        strokeDasharray="6 6"
                      />
                    ) : null,
                  )}
                  {datasetViews.map((view) =>
                    view.regressionData && !hiddenSeriesKeys.has(`${view.key}-regression`) ? (
                      <Line
                        key={`${view.key}-regression`}
                        type="linear"
                        tooltipType="none"
                        style={{ pointerEvents: 'none' }}
                        data={view.regressionData}
                        dataKey="y"
                        stroke={view.color}
                        strokeWidth={3}
                        dot={false}
                        isAnimationActive={false}
                        name={`${view.label} · ${curveLegendLabel}`}
                      />
                    ) : null,
                  )}
                  {datasetViews.flatMap((view) =>
                    view.scatterSeries.map((series) =>
                      hiddenSeriesKeys.has(series.key) ? null : (
                        <Scatter
                          key={series.key}
                          data={series.data}
                          name={series.label}
                          fill={series.color}
                          stroke="none"
                          legendType="circle"
                          shape={renderScatterPoint}
                          isAnimationActive={false}
                        />
                      ),
                    ),
                  )}
                  {datasetViews.map((view) =>
                    view.highlightSeries.length > 0 && !hiddenSeriesKeys.has(`${view.key}-highlight`) ? (
                      <Scatter
                        key={`${view.key}-highlight`}
                        data={view.highlightSeries}
                        name={t('race.scatter.series.highlight', 'Top VMG%')}
                        fill={COLOR_HIGHLIGHT}
                        stroke="none"
                        legendType="diamond"
                        shape={renderHighlightPoint}
                        isAnimationActive={false}
                      />
                    ) : null,
                  )}
                </ComposedChart>
              </ResponsiveContainer>
              {hoveredPoint ? (
                <div
                  className="scatter-panel__tooltip"
                  data-position-x={hoveredPoint.alignment.horizontal}
                  data-position-y={hoveredPoint.alignment.vertical}
                  style={{ left: hoveredPoint.position.left, top: hoveredPoint.position.top }}
                >
                  <ScatterTooltip
                    datum={hoveredPoint.datum}
                    xMetricLabel={formatMetricLabelNoUnit(xMetric)}
                    yMetricLabel={formatMetricLabelNoUnit(yMetric)}
                    phaseDuration={phaseDuration}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
        <ScatterLegendBar
          entries={legendEntries}
          hiddenKeys={hiddenSeriesKeys}
          onToggle={toggleSeriesVisibility}
        />
      </Stack>
    </div>
  );
};

export const ScatterPanel = memo(ScatterPanelComponent);
